/** @format */

import {
  Authenticator,
  Button,
  useAuthenticator,
  View,
} from "@aws-amplify/ui-react";
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  deleteUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  SignInInput,
  signOut,
  signUp,
  SignUpInput,
} from "aws-amplify/auth";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { track } from "../../lib/posthog";
import HeroSection from "../../components/ui/HeroSection";
import logoUrl from "../../assets/logo.png";
import {
  AUTH_CONSTANTS,
  sanitizeEmailForSubmit,
  validateEmailFormat,
  validateName,
  validatePasswordLength,
  validateVerificationCode,
} from "../../utils/authValidation";
import { API_UNREACHABLE_MESSAGE, AUTH_ORG_ORIGIN_ERROR_KEY } from "../../lib/orgOrigin";
import { pingApi } from "../../api/client";
import {
  useOrganizationStore,
  validateOrgsForCurrentOrigin,
} from "../../store/useOrganizationStore";
import "./Login.css";

const RESEND_COOLDOWN_SECONDS = 60;
const AUTH_ROUTE_STORAGE_KEY = "amplify-auth-route";
const INVALID_CREDENTIALS_ERROR = "Invalid email or password.";

function normalizeAuthErrorMessage(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name?: string }).name === "NotAuthorizedException"
  ) {
    return INVALID_CREDENTIALS_ERROR;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Unable to sign in right now. Please try again.";
}

function createResendCooldown() {
  let lastCallTime = 0;
  return {
    checkAndExecute<T>(fn: () => Promise<T>): Promise<T> {
      const now = Date.now();
      const elapsed = Math.floor((now - lastCallTime) / 1000);
      if (lastCallTime > 0 && elapsed < RESEND_COOLDOWN_SECONDS) {
        const remaining = RESEND_COOLDOWN_SECONDS - elapsed;
        throw new Error(
          `Please wait ${remaining} seconds before requesting a new code.`,
        );
      }
      return fn().then((result) => {
        lastCallTime = Date.now();
        return result;
      });
    },
  };
}

const signUpResendCooldown = createResendCooldown();
const resetPasswordResendCooldown = createResendCooldown();

function getPersistedAuthRoute():
  | "forgotPassword"
  | "signIn"
  | "signUp"
  | undefined {
  try {
    const stored = sessionStorage.getItem(AUTH_ROUTE_STORAGE_KEY);
    if (stored === "forgotPassword") return "forgotPassword";
  } catch {
    /* ignore */
  }
  return undefined;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authFormError, setAuthFormError] = useState<string | null>(null);
  const { authStatus, route, toSignIn } = useAuthenticator(
    (ctx) => [ctx.authStatus, ctx.route] as const,
  );

  const initialState = useMemo(() => getPersistedAuthRoute(), []);

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem(AUTH_ORG_ORIGIN_ERROR_KEY);
      if (msg) {
        setAuthFormError(msg);
        sessionStorage.removeItem(AUTH_ORG_ORIGIN_ERROR_KEY);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (route === "forgotPassword") {
      sessionStorage.setItem(AUTH_ROUTE_STORAGE_KEY, route);
    } else if (route === "signIn" || authStatus === "authenticated") {
      sessionStorage.removeItem(AUTH_ROUTE_STORAGE_KEY);
    }
  }, [route, authStatus]);

  const allowNumericKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const allowedKeys = [
        "Backspace",
        "Tab",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ];
      if (allowedKeys.includes(event.key)) return;
      if (
        (event.ctrlKey || event.metaKey) &&
        ["a", "c", "v", "x"].includes(event.key.toLowerCase())
      )
        return;
      if (!/^\d$/.test(event.key)) event.preventDefault();
    },
    [],
  );

  useEffect(() => {
    if (authStatus === "authenticated") {
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    }
  }, [authStatus, navigate, location.state]);

  const formFields = {
    signIn: {
      username: {
        placeholder: "Enter your email address",
        maxLength: AUTH_CONSTANTS.EMAIL_MAX_LENGTH,
      },
      password: {
        maxLength: AUTH_CONSTANTS.PASSWORD_MAX_LENGTH,
      },
    },
    signUp: {
      given_name: {
        order: 1,
        placeholder: "Enter your first name",
        label: "First Name",
        isRequired: true,
        maxLength: AUTH_CONSTANTS.NAME_MAX_LENGTH,
      },
      family_name: {
        order: 2,
        placeholder: "Enter your last name",
        label: "Last Name",
        isRequired: true,
        maxLength: AUTH_CONSTANTS.NAME_MAX_LENGTH,
      },
      email: {
        order: 3,
        isRequired: true,
        placeholder: "Enter your email address",
        label: "Email",
        maxLength: AUTH_CONSTANTS.EMAIL_MAX_LENGTH,
      },
      password: {
        order: 4,
        maxLength: AUTH_CONSTANTS.PASSWORD_MAX_LENGTH,
      },
      confirm_password: {
        order: 5,
        maxLength: AUTH_CONSTANTS.PASSWORD_MAX_LENGTH,
      },
    },
    confirmSignUp: {
      confirmation_code: {
        placeholder: "Enter the 6-digit code",
        label: "Verification code",
        isRequired: true,
        maxLength: AUTH_CONSTANTS.CONFIRMATION_CODE_LENGTH,
        pattern: "[0-9]*",
        inputMode: "numeric",
        autocomplete: "one-time-code",
        onKeyDown: allowNumericKeyDown,
      },
    },
    forceNewPassword: {
      password: {
        order: 1,
        placeholder: "Enter your new password",
        label: "New password",
        isRequired: true,
        maxLength: AUTH_CONSTANTS.PASSWORD_MAX_LENGTH,
      },
      confirm_password: {
        order: 2,
        placeholder: "Confirm your new password",
        label: "Confirm new password",
        isRequired: true,
        maxLength: AUTH_CONSTANTS.PASSWORD_MAX_LENGTH,
      },
    },
    forgotPassword: {
      username: {
        placeholder: "Enter your email address",
        maxLength: AUTH_CONSTANTS.EMAIL_MAX_LENGTH,
      },
    },
    confirmResetPassword: {
      confirmation_code: {
        placeholder: "Enter the 6-digit code",
        label: "Verification code",
        isRequired: true,
        maxLength: AUTH_CONSTANTS.CONFIRMATION_CODE_LENGTH,
        pattern: "[0-9]*",
        inputMode: "numeric",
        autocomplete: "one-time-code",
        onKeyDown: allowNumericKeyDown,
      },
    },
  };

  // Store the password temporarily for confirmation
  let tempPassword: string | null = null;

  const services = {
    async handleSignUp(input: SignUpInput) {
      let userCreated = false;
      try {
        const emailValidation = validateEmailFormat(input.username);
        if (!emailValidation.valid) {
          throw new Error(emailValidation.message);
        }

        const passwordValidation = validatePasswordLength(input.password ?? "");
        if (!passwordValidation.valid) {
          throw new Error(passwordValidation.message);
        }

        validateName(
          input.options?.userAttributes?.given_name ?? "",
          "first name",
        );
        validateName(
          input.options?.userAttributes?.family_name ?? "",
          "last name",
        );

        const sanitizedEmail = sanitizeEmailForSubmit(input.username);
        tempPassword = input.password ?? "";

        const result = await signUp({
          username: sanitizedEmail,
          password: tempPassword,
          options: {
            userAttributes: {
              email: sanitizedEmail,
              given_name: input.options?.userAttributes?.given_name?.trim() ?? "",
              family_name: input.options?.userAttributes?.family_name?.trim() ?? "",
            },
          },
        });
        userCreated = true;

        // Return the signup result - we'll handle validation after confirmation
        return result;
      } catch (error: Error | unknown) {
        console.error("Error during signup process:", error);

        // Handle specific error cases
        if (error instanceof Error) {
          if (error.name === "UsernameExistsException") {
            // User already exists, no need to clean up
            throw error;
          }
          if (error.name === "InvalidParameterException") {
            // Invalid parameters, no need to clean up
            throw error;
          }
          if (error.message?.includes("Exceeded daily email limit")) {
            throw new Error(
              "We've reached our daily email limit. Please try again later or contact support for assistance.",
            );
          }
        }

        // Only attempt cleanup if the user was actually created
        if (userCreated) {
          try {
            console.log("Attempting to clean up by deleting user...");
            await deleteUser();
            console.log("User deleted successfully during cleanup");
          } catch (deleteError) {
            console.error("Failed to delete user during cleanup:", deleteError);
          }
        }
        throw error;
      }
    },

    async handleConfirmSignUp(input: {
      username: string;
      confirmationCode: string;
    }) {
      try {
        const codeValidation = validateVerificationCode(input.confirmationCode);
        if (!codeValidation.valid) {
          throw new Error(codeValidation.message);
        }

        const sanitizedUsername = sanitizeEmailForSubmit(input.username);
        const sanitizedCode = input.confirmationCode.trim();

        const result = await confirmSignUp({
          username: sanitizedUsername,
          confirmationCode: sanitizedCode,
        });

        if (tempPassword) {
          await signIn({
            username: sanitizedUsername,
            password: tempPassword,
          });
          tempPassword = null;
        } else {
          toSignIn();
        }

        track("user_signed_up", { method: "email" });
        return result;
      } catch (error) {
        console.error("Error during confirmation process:", error);
        throw error;
      }
    },

    async handleSignIn(input: SignInInput) {
      if (tempPassword) {
        throw new Error("Please complete the confirmation process first");
      }
      setAuthFormError(null);
      try {
        sessionStorage.removeItem(AUTH_ORG_ORIGIN_ERROR_KEY);
      } catch {
        /* ignore */
      }
      const emailValidation = validateEmailFormat(input.username);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message);
      }
      const passwordValidation = validatePasswordLength(input.password ?? "");
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message);
      }

      // Step 1: API reachability — fail fast before touching Cognito.
      try {
        await pingApi();
      } catch {
        setAuthFormError(API_UNREACHABLE_MESSAGE);
        throw new Error(API_UNREACHABLE_MESSAGE);
      }

      // Step 2: Cognito credentials (existing behavior unchanged).
      let result;
      try {
        result = await signIn({
          username: sanitizeEmailForSubmit(input.username),
          password: input.password,
        });
      } catch (error) {
        setAuthFormError(normalizeAuthErrorMessage(error));
        throw error;
      }

      // If Cognito requires a new password (user created via admin_create_user),
      // the session is not yet complete — skip org validation here.
      // OrganizationBootstrap will load orgs after confirmSignIn completes.
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        return result;
      }

      // Step 3: Org/origin validation — must succeed before we let Amplify
      // flip authStatus to "authenticated".
      try {
        const orgs = await validateOrgsForCurrentOrigin();
        useOrganizationStore.getState().hydrateFromLogin(orgs);
      } catch (err) {
        try { await signOut(); } catch { /* ignore */ }
        // SECURITY: origin mismatch and 403 both surface as the same vague
        // message as a bad password to avoid leaking tenant info.
        const isOrgError =
          err &&
          typeof err === "object" &&
          "kind" in err &&
          ((err as { kind: string }).kind === "ORIGIN" ||
            (err as { kind: string }).kind === "FORBIDDEN");
        const message = isOrgError
          ? "Incorrect username or password"
          : API_UNREACHABLE_MESSAGE;
        setAuthFormError(message);
        throw new Error(message);
      }

      track("user_signed_in", { method: "email" });
      track("user_logged_in", { method: "email" });
      return result;
    },

    async handleConfirmSignInForceNewPassword({
      password,
    }: {
      password: string;
      confirm_password?: string;
    }) {
      const passwordValidation = validatePasswordLength(password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message);
      }
      return confirmSignIn({ challengeResponse: password });
    },

    async handleForgotPassword(input: { username: string }) {
      const emailValidation = validateEmailFormat(input.username);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message);
      }
      return resetPasswordResendCooldown.checkAndExecute(() =>
        resetPassword({
          username: sanitizeEmailForSubmit(input.username),
        }),
      );
    },

    async handleResendSignUpCode(input: { username: string }) {
      return signUpResendCooldown.checkAndExecute(() =>
        resendSignUpCode({
          username: sanitizeEmailForSubmit(input.username),
        }),
      );
    },

    async handleForgotPasswordSubmit(input: {
      username: string;
      confirmationCode: string;
      newPassword: string;
    }) {
      const emailValidation = validateEmailFormat(input.username);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message);
      }

      const codeValidation = validateVerificationCode(input.confirmationCode);
      if (!codeValidation.valid) {
        throw new Error(codeValidation.message);
      }

      const passwordValidation = validatePasswordLength(input.newPassword);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message);
      }

      return confirmResetPassword({
        username: sanitizeEmailForSubmit(input.username),
        confirmationCode: input.confirmationCode.trim(),
        newPassword: input.newPassword,
      });
    },
  };

  const authenticatorComponents = {
    ConfirmResetPassword: {
      Footer() {
        return (
          <View textAlign="center" padding="0 0 0 0">
            <Button
              fontWeight="normal"
              onClick={toSignIn}
              size="small"
              variation="link"
            >
              Back to Sign In
            </Button>
          </View>
        );
      },
    },
  };

  return (
    <div className="login-page">
      <div className="login-hero-panel">
        <HeroSection
          title="ManufactureAI"
          description="AI-powered manufacturing documentation"
          className="login-hero"
          brandImage={logoUrl}
          brandImageAlt="ManufactureAI"
        />
      </div>
      <div className="login-page-content">
        <div className="login-container">
          <div className="login-auth">
            <Authenticator
              components={authenticatorComponents}
              formFields={formFields}
              initialState={initialState}
              services={services}
              hideSignUp
            />
          </div>
          {authFormError ? (
            <div className="login-auth-form-error" role="alert">
              {authFormError}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
